/**
 * SourceCard — Source preview card
 *
 * Shows source info: domain, favicon, title, snippet.
 * Trust scoring removed (2026-02-19) — sources displayed without trust metrics.
 */
import React, { useState } from 'react';
import type { SourceCardData } from '../types';

// =============================================================================
// HELPERS
// =============================================================================

function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
}

// =============================================================================
// PLACEHOLDER IMAGE
// =============================================================================

const PlaceholderImage: React.FC<{ domain: string }> = ({ domain }) => (
  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-900/30 to-black/60">
    <span className="text-3xl font-bold text-cyan-400/20 tracking-widest uppercase">
      {domain.split('.')[0]?.slice(0, 4)}
    </span>
  </div>
);

// =============================================================================
// SOURCE CARD
// =============================================================================

interface SourceCardProps {
  data: SourceCardData;
  onAction?: (prompt: string) => void;
}

export const SourceCard: React.FC<SourceCardProps> = ({ data, onAction }) => {
  const { source, compact } = data;
  const [imgError, setImgError] = useState(false);

  const handleOpenSource = () => {
    window.open(source.url, '_blank', 'noopener,noreferrer');
  };

  if (compact) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-black/40 border border-cyan-400/15 hover:border-cyan-400/30 transition-all duration-200 group cursor-pointer"
        onClick={handleOpenSource}
      >
        <img
          src={source.favicon || faviconUrl(source.domain)}
          alt={source.domain}
          className="w-4 h-4 rounded-sm shrink-0"
          onError={(e) => {
            (e.target as HTMLImageElement).src = faviconUrl(source.domain);
          }}
        />
        <span className="text-sm text-cyan-300/80 group-hover:text-cyan-200 truncate flex-1 font-medium">
          {source.title}
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-black/50 border border-cyan-400/20 hover:border-cyan-400/40 overflow-hidden transition-all duration-300 shadow-[0_0_15px_rgba(34,211,238,0.05)] hover:shadow-[0_0_25px_rgba(34,211,238,0.12)] group">
      {/* OG Image Preview */}
      <div className="relative w-full h-32 overflow-hidden bg-black/30">
        {source.ogImage && !imgError ? (
          <img
            src={source.ogImage}
            alt={source.title}
            className="w-full h-full object-cover opacity-70 group-hover:opacity-85 transition-opacity duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <PlaceholderImage domain={source.domain} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="px-4 pt-3 pb-4 space-y-2.5">
        <div className="flex items-center gap-2">
          <img
            src={source.favicon || faviconUrl(source.domain)}
            alt={source.domain}
            className="w-4 h-4 rounded-sm shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).src = faviconUrl(source.domain);
            }}
          />
          <span className="text-xs text-cyan-400/60 font-medium truncate">
            {source.domain}
          </span>
        </div>

        <h3 className="text-sm font-semibold text-cyan-200 leading-snug line-clamp-2 group-hover:text-cyan-100 transition-colors">
          {source.title}
        </h3>

        {source.snippet && (
          <p className="text-xs text-cyan-400/50 leading-relaxed line-clamp-2">
            {source.snippet}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleOpenSource}
            className="px-3 py-1.5 text-xs font-medium text-cyan-300 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/20 hover:border-cyan-400/40 rounded-lg transition-all duration-200"
          >
            Visit Source
          </button>
          {onAction && (
            <button
              onClick={() => onAction(`Cite source [${source.id}]: ${source.title}`)}
              className="px-3 py-1.5 text-xs font-medium text-cyan-400/50 hover:text-cyan-300 bg-transparent hover:bg-cyan-400/5 border border-cyan-400/10 hover:border-cyan-400/20 rounded-lg transition-all duration-200"
            >
              Cite
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceCard;
